function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/*
 * Audiences: Duplicates tab (redesign 2026-10-09, PlunkDocs mockups/audiences-redesign-2026-10-09.html board 5).
 * Left: pairs that are probably the same person (tick to merge several). Right: side-by-side preview of the
 * selected pair with the record that is kept, Merge / Not the same person / Keep the other one.
 *
 *   GET  /v1/person/duplicates?status&minScore&audienceId&$top&$skip   {items:[{id, personA, personB, score, reasonsJson,
 *        status, decidedBy, decidedDate, nameA, nameB, emailA, emailB, createdA, createdB, createdDate}], count}
 *   POST /v1/person/duplicates/{id}/merge {survivorId?, strategy?}  → PersonMergeRecord {id, survivorId, mergedId, consentChanged}
 *   POST /v1/person/duplicates/{id}/dismiss
 *   POST /v1/person/duplicates/merge-auto → {considered, merged, failed}
 *   POST /v1/person/merges/{mergeId}/undo
 *   GET|PUT /v1/person/dedupe-policy {enabled, autoMergeRules[], minScore, strategy}  (PUT = Administrator)
 *
 * Record cards read identity from GET /v1/activity/contacts/{id}; when that read fails the card falls back to
 * the pair's own name/email columns. Every id is a string (api.js reviver).
 */
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Pending, PlunkDropDown } from "../../components/analytics-kit";
import Error from "../../components/error";
import { num } from "../sales/territory-common";
import AudiencesShell, { SUBTITLES } from "./shell";
import { getDuplicates, mergeCandidate, dismissCandidate, undoMerge, getDedupePolicy, putDedupePolicy, getPersonSummary, mergeAutoDuplicates, listAudiences, isAdministrator, isSoft, toast } from "./api";
import { Modal, InlineConfirm, Banner, ErrorLine, fmtDate } from "./ui";
const PAGE = 50;
const OTHER_RULES = ["phone_e164", "customer_external", "email_plus_suffix", "name_phone", "name_postal"];
const RULE_PLAIN = {
  email_exact: "Same email address",
  phone_e164: "Same phone number",
  customer_external: "Same customer number",
  email_plus_suffix: "Emails differ only by a +tag (like name+shop@)",
  name_phone: "Same name and phone number",
  name_postal: "Same name and postal code"
};
const LIKELY = [[1, "Certain (exact matches only)"], [0.9, "Very likely"], [0.8, "Likely"], [0.7, "Possible"]];
const KEEP = [["most_complete", "The most complete one"], ["newest", "The newest"], ["oldest", "The oldest"]];
function rules(c) {
  try {
    const r = typeof c.reasonsJson === "string" ? JSON.parse(c.reasonsJson) : c.reasonsJson || [];
    return Array.isArray(r) ? r.map(x => typeof x === "string" ? x : x && x.rule).filter(Boolean) : [];
  } catch (e) {
    return [];
  }
}
const joinAnd = xs => xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
const norm = s => String(s || "").trim().toLowerCase();

/** "+shop" when one email is the other with a plus tag (morgan+shop@x vs morgan@x). */
function plusTag(a, b) {
  const split = e => {
    const m = norm(e).match(/^([^@+]+)(\+[^@]*)?@(.+)$/);
    return m ? {
      base: m[1],
      tag: m[2] || "",
      dom: m[3]
    } : null;
  };
  const x = split(a),
    y = split(b);
  if (!x || !y || x.base !== y.base || x.dom !== y.dom) return null;
  return x.tag && !y.tag ? x.tag : y.tag && !x.tag ? y.tag : null;
}

/** Plain sentence for why two records look like one person. */
function reasonText(c) {
  const r = rules(c);
  const sameName = c.nameA && c.nameB && norm(c.nameA) === norm(c.nameB);
  const things = [];
  if (r.includes("name_phone") || r.includes("name_postal") || sameName && r.includes("email_plus_suffix")) things.push("name");
  if (r.includes("email_exact")) things.push("email address");
  if (r.includes("phone_e164") || r.includes("name_phone")) things.push("phone number");
  if (r.includes("customer_external")) things.push("customer number");
  if (r.includes("name_postal")) things.push("postal code");
  if (!things.length && sameName) things.push("name");
  const parts = [];
  if (things.length) parts.push(`Same ${joinAnd(things)}`);
  if (r.includes("email_plus_suffix")) {
    const tag = plusTag(c.emailA, c.emailB);
    parts.push(`${parts.length ? "emails" : "Emails"} differ only by ${tag || "a +tag"}`);
  }
  return parts.length ? parts.join(", ") : "Similar details";
}
function likelihood(score) {
  const s = Number(score);
  if (!isFinite(s)) return null;
  return s >= 0.9 ? "Very likely" : s >= 0.8 ? "Likely" : "Possible";
}
function nearestLikely(v) {
  const s = Number(v);
  if (!isFinite(s)) return 1;
  return LIKELY.reduce((best, [x]) => Math.abs(x - s) < Math.abs(best - s) ? x : best, 1);
}
const pairName = c => `${c.nameA || c.emailA || "Unnamed"} and ${c.nameB || c.emailB || "Unnamed"}`;
class DuplicatesComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "setStatus", status => this.setState({
      status,
      selected: {},
      preview: null
    }, () => this.load()));
    _defineProperty(this, "setList", listId => this.setState({
      listId,
      selected: {},
      preview: null
    }, () => {
      this.load();
      this.loadCounts();
      this.loadAutoSafe();
    }));
    _defineProperty(this, "mergeAuto", async () => {
      this.setState({
        autoBusy: true
      });
      try {
        const r = await mergeAutoDuplicates(this.state.listId ? {
          audienceId: String(this.state.listId)
        } : {});
        const merged = Number(r && r.merged) || 0,
          failed = Number(r && r.failed) || 0;
        toast(`Merged ${num(merged)} ${merged === 1 ? "pair" : "pairs"} for you${failed ? `, ${num(failed)} could not be merged` : ""}.`);
        this.setState({
          confirmAuto: false,
          autoBusy: false,
          selected: {}
        });
        this.load(0);
        this.loadCounts();
        this.loadAutoSafe();
      } catch (e) {
        this.setState({
          confirmAuto: false,
          autoBusy: false
        });
        toast(isSoft(e) ? "Automatic merging is not available on this workspace yet." : e.status === 403 || e.status === 401 ? "Only an Administrator can do this." : e.message || "Merging failed");
      }
    });
    _defineProperty(this, "toggle", c => this.setState(s => {
      const selected = {
        ...s.selected
      };
      const k = String(c.id);
      if (selected[k]) delete selected[k];else selected[k] = true;
      return {
        selected
      };
    }));
    _defineProperty(this, "openPreview", async c => {
      this.setState({
        preview: c,
        previewPersons: null,
        survivor: this.defaultKeep(c, []),
        survivorManual: false,
        err: ""
      });
      const ids = [String(c.personA), String(c.personB)];
      const persons = await Promise.all(ids.map(id => getPersonSummary(id).catch(() => null)));
      if (!this._alive || !this.state.preview || String(this.state.preview.id) !== String(c.id)) return;
      this.setState(s => ({
        previewPersons: {
          [ids[0]]: persons[0],
          [ids[1]]: persons[1]
        },
        survivor: s.survivorManual ? s.survivor : this.defaultKeep(c, persons)
      }));
    });
    _defineProperty(this, "merge", async (c, survivorId) => {
      this.setState({
        busy: true,
        err: ""
      });
      try {
        const rec = await mergeCandidate(String(c.id), survivorId ? {
          survivorId: String(survivorId),
          strategy: "manual"
        } : {});
        const survivorName = (String(rec.survivorId) === String(c.personA) ? c.nameA : c.nameB) || "the kept record";
        this.setState({
          preview: null,
          lastMerge: {
            mergeId: String(rec.id),
            candidateId: String(c.id),
            survivorName,
            consentChanged: !!rec.consentChanged
          },
          selected: {}
        });
        toast(`Merged into ${survivorName}. You can undo for 30 days.`);
        this.load(this.state.skip);
        this.loadCounts();
      } catch (e) {
        this.setState({
          err: e.message || "Merge failed"
        });
      } finally {
        if (this._alive) this.setState({
          busy: false
        });
      }
    });
    _defineProperty(this, "mergeSelected", async () => {
      const ids = Object.keys(this.state.selected);
      if (!ids.length) return;
      this.setState({
        busy: true,
        err: ""
      });
      let ok = 0,
        fail = 0,
        last = null,
        lastId = null;
      for (const id of ids) {
        try {
          last = await mergeCandidate(id, {});
          lastId = id;
          ok++;
        } catch (e) {
          fail++;
        }
      }
      this.setState({
        busy: false,
        selected: {},
        lastMerge: ok === 1 && last ? {
          mergeId: String(last.id),
          candidateId: lastId,
          survivorName: "the kept record"
        } : null
      });
      toast(`Merged ${num(ok)} ${ok === 1 ? "pair" : "pairs"}${fail ? `, ${num(fail)} could not be merged` : ""}.`);
      this.load(this.state.skip);
      this.loadCounts();
    });
    _defineProperty(this, "dismiss", async ids => {
      if (!ids.length) return;
      this.setState({
        busy: true,
        err: ""
      });
      let ok = 0;
      for (const id of ids) {
        try {
          await dismissCandidate(id);
          ok++;
        } catch (e) {/* counted below */}
      }
      this.setState({
        busy: false,
        selected: {},
        preview: null
      });
      toast(`Marked ${num(ok)} ${ok === 1 ? "pair" : "pairs"} as not the same person.`);
      this.load(this.state.skip);
      this.loadCounts();
    });
    _defineProperty(this, "undo", async () => {
      const m = this.state.lastMerge;
      if (!m) return;
      try {
        await undoMerge(m.mergeId);
        toast("Merge undone. Activity recorded since the merge stays on the kept record.");
        this.setState({
          lastMerge: null
        });
        this.load(this.state.skip);
        this.loadCounts();
      } catch (e) {
        toast(e.message || "Undo failed");
      }
    });
    _defineProperty(this, "openPolicy", () => {
      const p = this.state.policy || {
        enabled: true,
        autoMergeRules: ["email_exact"],
        minScore: 1,
        strategy: "most_complete"
      };
      this.setState({
        policyOpen: true,
        policyErr: "",
        policyDraft: {
          ...p,
          autoMergeRules: p.enabled ? [...(p.autoMergeRules || [])] : [],
          minScore: nearestLikely(p.minScore)
        }
      });
    });
    _defineProperty(this, "savePolicy", async () => {
      const d = this.state.policyDraft;
      const ruleList = d.autoMergeRules || [];
      this.setState({
        policyErr: "",
        busy: true
      });
      try {
        const saved = await putDedupePolicy({
          enabled: ruleList.length > 0,
          autoMergeRules: ruleList,
          minScore: Number(d.minScore) || 1,
          strategy: d.strategy || "most_complete"
        });
        this.setState({
          policy: saved,
          policyOpen: false
        });
        this.loadAutoSafe(saved);
        toast("Settings saved.");
      } catch (e) {
        this.setState({
          policyErr: e.status === 403 || e.status === 401 ? "Only an Administrator can change these settings." : e.message || "Save failed"
        });
      } finally {
        if (this._alive) this.setState({
          busy: false
        });
      }
    });
    this.state = {
      status: "pending",
      data: null,
      counts: {},
      error: false,
      skip: 0,
      selected: {},
      preview: null,
      previewPersons: null,
      survivor: null,
      survivorManual: false,
      busy: false,
      err: "",
      policy: undefined,
      policyOpen: false,
      policyDraft: null,
      policyErr: "",
      lastMerge: null,
      // {mergeId, candidateId, survivorName, consentChanged}
      lists: null,
      listId: "",
      autoSafe: undefined,
      confirmAuto: false,
      autoBusy: false
    };
    this._alive = true;
  }
  componentDidMount() {
    this.load();
    this.loadCounts();
    this.loadPolicy();
    listAudiences().then(r => {
      if (this._alive) this.setState({
        lists: Array.isArray(r) ? r : r && r.items || []
      });
    }).catch(() => {
      if (this._alive) this.setState({
        lists: []
      });
    });
  }
  componentWillUnmount() {
    this._alive = false;
  }
  async load(skip = 0) {
    this.setState({
      data: null,
      skip
    });
    try {
      const r = await getDuplicates(this.state.status, undefined, PAGE, skip, this.state.listId || undefined);
      if (!this._alive) return;
      const data = r || {
        items: [],
        count: 0
      };
      this.setState({
        data
      });
      // keep the preview on a pair that is still in the queue, else select the first one
      const items = data.items || [];
      if (this.state.status === "pending") {
        const cur = this.state.preview && items.find(x => String(x.id) === String(this.state.preview.id));
        if (!cur) {
          if (items.length) this.openPreview(items[0]);else this.setState({
            preview: null
          });
        }
      }
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  async loadCounts() {
    const out = {};
    await Promise.all(["pending", "merged", "auto_merged", "dismissed"].map(async s => {
      try {
        const r = await getDuplicates(s, undefined, 1, 0, this.state.listId || undefined);
        out[s] = Number(r && r.count) || 0;
      } catch (e) {
        out[s] = null;
      }
    }));
    if (this._alive) this.setState({
      counts: out
    });
  }
  async loadPolicy() {
    try {
      const p = await getDedupePolicy();
      if (this._alive) {
        this.setState({
          policy: p
        });
        this.loadAutoSafe(p);
      }
    } catch (e) {
      if (this._alive) this.setState({
        policy: null,
        autoSafe: null
      });
    }
  }

  /** How many waiting pairs the policy would merge now (the server re-checks the rules). */
  async loadAutoSafe(p) {
    const pol = p || this.state.policy;
    if (!pol || !pol.enabled || !(pol.autoMergeRules || []).length) {
      this.setState({
        autoSafe: 0
      });
      return;
    }
    try {
      const r = await getDuplicates("pending", pol.minScore != null ? String(pol.minScore) : undefined, 1, 0, this.state.listId || undefined);
      if (this._alive) this.setState({
        autoSafe: Number(r && r.count) || 0
      });
    } catch (e) {
      if (this._alive) this.setState({
        autoSafe: null
      });
    }
  }
  /** Default record to keep, following the workspace setting (most complete / newest / oldest). */
  defaultKeep(c, persons) {
    const ids = [String(c.personA), String(c.personB)];
    const strategy = this.state.policy && this.state.policy.strategy || "most_complete";
    const older = c.createdA && c.createdB && new Date(c.createdB) < new Date(c.createdA) ? 1 : 0;
    if (strategy === "oldest") return ids[older];
    if (strategy === "newest") return ids[1 - older];
    const fill = p => p ? (p.firstName ? 1 : 0) + (p.lastName ? 1 : 0) + (p.email || []).length + (p.phone || []).length + (p.address || []).length + ((p.audience || []).length ? 1 : 0) : 0;
    const sA = fill(persons[0]),
      sB = fill(persons[1]);
    return sB > sA ? ids[1] : sB === sA ? ids[older] : ids[0];
  }
  renderPolicyDialog() {
    const d = this.state.policyDraft;
    if (!d) return null;
    const admin = isAdministrator();
    const has = r => (d.autoMergeRules || []).includes(r);
    const toggleRule = r => this.setState({
      policyDraft: {
        ...d,
        autoMergeRules: has(r) ? d.autoMergeRules.filter(x => x !== r) : [...(d.autoMergeRules || []), r]
      }
    });
    return /*#__PURE__*/React.createElement(Modal, {
      title: "Duplicate settings",
      width: 540,
      onClose: () => this.setState({
        policyOpen: false
      }),
      busy: this.state.busy,
      note: admin ? "These settings apply to the whole workspace." : "Only an Administrator can change these settings.",
      footer: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
        onClick: () => this.setState({
          policyOpen: false
        })
      }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
        themeColor: "primary",
        disabled: !admin || this.state.busy,
        onClick: this.savePolicy
      }, "Save"))
    }, /*#__PURE__*/React.createElement("div", {
      className: "adu-policy"
    }, /*#__PURE__*/React.createElement("label", {
      className: "adu-check main"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      disabled: !admin,
      checked: has("email_exact"),
      onChange: () => toggleRule("email_exact")
    }), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "Merge exact email matches for me"), /*#__PURE__*/React.createElement("small", null, "Two records with the same email address are the same mailbox, so this is safe."))), /*#__PURE__*/React.createElement("div", {
      className: "adu-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "adu-label"
    }, "Also merge for me when"), OTHER_RULES.map(r => /*#__PURE__*/React.createElement("label", {
      key: r,
      className: "adu-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      disabled: !admin,
      checked: has(r),
      onChange: () => toggleRule(r)
    }), /*#__PURE__*/React.createElement("span", null, RULE_PLAIN[r]))), /*#__PURE__*/React.createElement("small", {
      className: "adu-muted"
    }, "Anything not merged for you waits under To review.")), /*#__PURE__*/React.createElement("label", {
      className: "adu-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "adu-label"
    }, "Only merge for me when a pair is at least"), /*#__PURE__*/React.createElement("select", {
      className: "adu-select",
      disabled: !admin,
      value: String(d.minScore),
      onChange: e => this.setState({
        policyDraft: {
          ...d,
          minScore: Number(e.target.value)
        }
      })
    }, LIKELY.map(([v, l]) => /*#__PURE__*/React.createElement("option", {
      key: v,
      value: String(v)
    }, l)))), /*#__PURE__*/React.createElement("label", {
      className: "adu-field"
    }, /*#__PURE__*/React.createElement("span", {
      className: "adu-label"
    }, "Which record to keep"), /*#__PURE__*/React.createElement("select", {
      className: "adu-select",
      disabled: !admin,
      value: d.strategy || "most_complete",
      onChange: e => this.setState({
        policyDraft: {
          ...d,
          strategy: e.target.value
        }
      })
    }, KEEP.map(([v, l]) => /*#__PURE__*/React.createElement("option", {
      key: v,
      value: v
    }, l))))), /*#__PURE__*/React.createElement(ErrorLine, null, this.state.policyErr));
  }
  recordRows(c, id) {
    const p = this.state.previewPersons ? this.state.previewPersons[id] : undefined;
    const isA = String(c.personA) === id;
    const emails = p ? (p.email || []).map(e => e.emailAddress).filter(Boolean) : [];
    const fallbackEmail = isA ? c.emailA : c.emailB;
    const phones = p ? (p.phone || []).map(e => e.phoneNumber).filter(Boolean) : [];
    const exp = p && (p.experience || [])[0];
    const company = p && p.company && (p.company.name || p.company) || exp && exp.company && (exp.company.name || exp.company) || null;
    const lists = p ? (p.audience || []).map(a => a.name || a.audienceName || (typeof a === "string" ? a : null)).filter(Boolean) : [];
    const n = k => p && isFinite(Number(p[k])) && p[k] != null ? Number(p[k]) : null;
    const visits = n("sessions") != null ? n("sessions") : n("visits");
    const orders = n("orders") != null ? n("orders") : n("conversions");
    const activity = [visits != null ? `${num(visits)} ${visits === 1 ? "visit" : "visits"}` : null, orders ? `${num(orders)} ${orders === 1 ? "order" : "orders"}` : null].filter(Boolean).join(", ");
    const created = isA ? c.createdA : c.createdB;
    return {
      name: p && p.fullName || (isA ? c.nameA : c.nameB) || "Unnamed",
      Email: emails.length ? emails.join(", ") : fallbackEmail || null,
      Phone: phones.length ? phones.join(", ") : null,
      Company: company,
      Lists: lists.length ? lists.join(", ") : null,
      Activity: activity || null,
      Added: created ? fmtDate(created) : null
    };
  }
  renderPreview() {
    const {
      preview: c,
      survivor,
      busy,
      err,
      previewPersons
    } = this.state;
    const a = String(c.personA),
      b = String(c.personB);
    const keep = survivor || a;
    const other = keep === a ? b : a;
    const recs = {
      [a]: this.recordRows(c, a),
      [b]: this.recordRows(c, b)
    };
    const fields = ["Email", "Phone", "Company", "Lists", "Activity", "Added"].filter(f => recs[a][f] || recs[b][f]);
    const keepName = recs[keep].name;
    const keepFirst = String(keepName).split(/\s+/)[0];
    const likely = likelihood(c.score);
    const keepIdx = keep === a ? 1 : 2;
    return /*#__PURE__*/React.createElement("section", {
      className: "adu-card adu-preview"
    }, /*#__PURE__*/React.createElement("div", {
      className: "adu-head"
    }, /*#__PURE__*/React.createElement("b", {
      className: "adu-title"
    }, keepName), /*#__PURE__*/React.createElement("span", {
      className: "adu-muted"
    }, reasonText(c), ".", likely ? ` ${likely === "Possible" ? "Possibly" : likely} the same person.` : "")), /*#__PURE__*/React.createElement("div", {
      className: "adu-records"
    }, [a, b].map((id, i) => {
      const r = recs[id];
      const kept = id === keep;
      return /*#__PURE__*/React.createElement("div", {
        key: id,
        className: `adu-record${kept ? " keep" : ""}`
      }, /*#__PURE__*/React.createElement("div", {
        className: "adu-record-head"
      }, /*#__PURE__*/React.createElement("b", null, /*#__PURE__*/React.createElement(Link, {
        to: `/Contacts/Detail/${encodeURIComponent(id)}`
      }, "Record ", i + 1)), kept ? /*#__PURE__*/React.createElement("span", {
        className: "adu-keep"
      }, "Keep") : null), previewPersons === null ? /*#__PURE__*/React.createElement("div", {
        className: "adu-row"
      }, /*#__PURE__*/React.createElement("span", {
        className: "v adu-muted"
      }, "Loading details\u2026")) : null, fields.map(f => /*#__PURE__*/React.createElement("div", {
        key: f,
        className: "adu-row"
      }, /*#__PURE__*/React.createElement("span", {
        className: "k"
      }, f), /*#__PURE__*/React.createElement("span", {
        className: "v"
      }, r[f] || "None"))));
    })), /*#__PURE__*/React.createElement("span", {
      className: "adu-muted adu-after"
    }, "After merging, ", keepFirst, " keeps the email from Record ", keepIdx, ", every list and all activity from both records. You can undo for 30 days."), /*#__PURE__*/React.createElement(ErrorLine, null, err), /*#__PURE__*/React.createElement("div", {
      className: "adu-actions"
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        survivor: other,
        survivorManual: true
      }),
      disabled: busy
    }, "Keep the other one"), /*#__PURE__*/React.createElement("span", {
      className: "adu-spacer"
    }), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.dismiss([String(c.id)]),
      disabled: busy
    }, "Not the same person"), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      onClick: () => this.merge(c, this.state.survivorManual ? keep : null),
      disabled: busy
    }, busy ? "Merging…" : "Merge")));
  }
  renderPager(count) {
    const {
      skip
    } = this.state;
    if (count <= PAGE) return null;
    return /*#__PURE__*/React.createElement("span", {
      className: "adu-pager"
    }, /*#__PURE__*/React.createElement(Button, {
      disabled: skip === 0,
      onClick: () => this.load(Math.max(0, skip - PAGE))
    }, "Previous"), /*#__PURE__*/React.createElement("span", {
      className: "adu-muted"
    }, num(skip + 1), " to ", num(Math.min(skip + PAGE, count)), " of ", num(count)), /*#__PURE__*/React.createElement(Button, {
      disabled: skip + PAGE >= count,
      onClick: () => this.load(skip + PAGE)
    }, "Next"));
  }
  renderQueue(items, count) {
    const {
      selected,
      preview,
      busy
    } = this.state;
    const nSel = Object.keys(selected).length;
    return /*#__PURE__*/React.createElement("div", {
      className: "adu-cols"
    }, /*#__PURE__*/React.createElement("section", {
      className: "adu-card adu-list"
    }, /*#__PURE__*/React.createElement("b", {
      className: "adu-card-title"
    }, "Probably the same person"), /*#__PURE__*/React.createElement("div", {
      className: "adu-pairs"
    }, items.map(c => {
      const k = String(c.id);
      const on = preview && String(preview.id) === k;
      const likely = likelihood(c.score);
      return /*#__PURE__*/React.createElement("div", {
        key: k,
        className: `adu-pair${on ? " on" : ""}`,
        role: "button",
        tabIndex: 0,
        onClick: () => this.openPreview(c),
        onKeyDown: e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.openPreview(c);
          }
        }
      }, /*#__PURE__*/React.createElement("input", {
        type: "checkbox",
        className: "adu-cb",
        "aria-label": `Tick ${pairName(c)}`,
        checked: !!selected[k],
        onClick: e => e.stopPropagation(),
        onChange: () => this.toggle(c)
      }), /*#__PURE__*/React.createElement("div", {
        className: "adu-pair-text"
      }, /*#__PURE__*/React.createElement("b", null, pairName(c)), /*#__PURE__*/React.createElement("span", {
        className: "adu-muted"
      }, reasonText(c))), likely ? /*#__PURE__*/React.createElement("span", {
        className: `adu-likely ${likely === "Very likely" ? "hi" : likely === "Likely" ? "mid" : "lo"}`
      }, likely) : null);
    })), /*#__PURE__*/React.createElement("div", {
      className: "adu-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "adu-muted adu-grow"
    }, nSel ? `${num(nSel)} ticked` : "Tick several to merge them together"), this.renderPager(count), /*#__PURE__*/React.createElement(Button, {
      disabled: !nSel || busy,
      onClick: () => this.dismiss(Object.keys(selected))
    }, "Not the same"), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: !nSel || busy,
      onClick: this.mergeSelected
    }, "Merge ticked"))), preview ? this.renderPreview() : /*#__PURE__*/React.createElement("section", {
      className: "adu-card adu-preview"
    }, /*#__PURE__*/React.createElement(Pending, null)));
  }
  renderDone(items, count) {
    const {
      status,
      lastMerge
    } = this.state;
    const isMerged = status === "merged" || status === "auto_merged";
    return /*#__PURE__*/React.createElement("section", {
      className: "adu-card adu-done"
    }, /*#__PURE__*/React.createElement("div", {
      className: "adu-done-head"
    }, /*#__PURE__*/React.createElement("b", {
      className: "adu-card-title"
    }, isMerged ? "Merged" : "Marked as not the same person"), isMerged ? /*#__PURE__*/React.createElement("select", {
      className: "adu-select",
      value: status,
      onChange: e => this.setStatus(e.target.value),
      "aria-label": "Who merged"
    }, /*#__PURE__*/React.createElement("option", {
      value: "merged"
    }, "Merged by your team"), /*#__PURE__*/React.createElement("option", {
      value: "auto_merged"
    }, "Merged for you")) : null), items.length === 0 ? /*#__PURE__*/React.createElement("div", {
      className: "adu-empty"
    }, /*#__PURE__*/React.createElement("b", null, "Nothing here yet"), /*#__PURE__*/React.createElement("span", {
      className: "adu-muted"
    }, isMerged ? "Pairs you merge show up here." : "Pairs you mark as not the same show up here, and are not suggested again.")) : /*#__PURE__*/React.createElement("div", {
      className: "adu-pairs"
    }, items.map(c => {
      const k = String(c.id);
      const when = c.decidedDate || c.createdDate;
      const canUndo = isMerged && lastMerge && lastMerge.candidateId === k;
      return /*#__PURE__*/React.createElement("div", {
        key: k,
        className: "adu-pair static"
      }, /*#__PURE__*/React.createElement("div", {
        className: "adu-pair-text"
      }, /*#__PURE__*/React.createElement("b", null, pairName(c)), /*#__PURE__*/React.createElement("span", {
        className: "adu-muted"
      }, reasonText(c), when ? ` · ${fmtDate(when)}` : "", c.decidedBy ? ` · ${c.decidedBy}` : "")), canUndo ? /*#__PURE__*/React.createElement(Button, {
        onClick: this.undo
      }, "Undo") : null);
    })), count > PAGE ? /*#__PURE__*/React.createElement("div", {
      className: "adu-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "adu-grow"
    }), this.renderPager(count)) : null);
  }
  render() {
    const {
      data,
      error,
      counts,
      status,
      policy,
      lastMerge,
      lists,
      listId,
      autoSafe,
      confirmAuto,
      autoBusy
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    const items = data ? data.items || [] : [];
    const count = data ? Number(data.count) || 0 : 0;
    const isMerged = status === "merged" || status === "auto_merged";
    const cnt = (k, sel) => {
      if (k === "merged") {
        const m = counts.merged,
          am = counts.auto_merged;
        if (m != null || am != null) return (m || 0) + (am || 0);
      } else if (counts[k] != null) return counts[k];
      return sel && data ? count : null;
    };
    const pills = [["pending", "To review", status === "pending"], ["merged", "Merged", isMerged], ["dismissed", "Not the same", status === "dismissed"]];
    const autoOn = policy && policy.enabled && (policy.autoMergeRules || []).includes("email_exact");
    const admin = isAdministrator();
    return /*#__PURE__*/React.createElement(AudiencesShell, {
      subtitle: SUBTITLES.duplicates,
      cls: "adu-view"
    }, lastMerge ? /*#__PURE__*/React.createElement(Banner, {
      action: {
        label: "Undo",
        onClick: this.undo
      },
      onClose: () => this.setState({
        lastMerge: null
      })
    }, "Merged into ", lastMerge.survivorName, ".", lastMerge.consentChanged ? " Their email preferences changed to the stricter choice." : "") : null, /*#__PURE__*/React.createElement("div", {
      className: "adu-top"
    }, pills.map(([k, label, on]) => {
      const n = cnt(k, on);
      return /*#__PURE__*/React.createElement("button", {
        key: k,
        type: "button",
        className: `adu-pill${on ? " on" : ""}`,
        "aria-pressed": on,
        onClick: () => this.setStatus(k)
      }, label, n != null ? ` ${num(n)}` : "");
    }), lists && lists.length ? /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "200px",
      value: listId,
      onChange: this.setList,
      options: [["", "All lists"], ...lists.map(l => [String(l.id), l.name])]
    }) : null, /*#__PURE__*/React.createElement("span", {
      className: "adu-grow"
    }), autoOn ? /*#__PURE__*/React.createElement("span", {
      className: "adu-muted"
    }, "Exact email matches are merged for you") : null, status === "pending" && admin && policy && policy.enabled && autoSafe ? confirmAuto ? /*#__PURE__*/React.createElement(InlineConfirm, {
      question: `Merge ${num(autoSafe)} matching ${autoSafe === 1 ? "pair" : "pairs"} now?`,
      yes: "Yes, merge",
      no: "Cancel",
      busy: autoBusy,
      onYes: this.mergeAuto,
      onNo: () => this.setState({
        confirmAuto: false
      })
    }) : /*#__PURE__*/React.createElement(Button, {
      disabled: autoBusy,
      onClick: () => this.setState({
        confirmAuto: true
      })
    }, "Merge matches now (", num(autoSafe), ")") : null, /*#__PURE__*/React.createElement(Button, {
      className: "adu-settings",
      onClick: this.openPolicy
    }, "Settings")), !data ? /*#__PURE__*/React.createElement(Pending, null) : status === "pending" ? items.length === 0 ? /*#__PURE__*/React.createElement("section", {
      className: "adu-card adu-empty"
    }, /*#__PURE__*/React.createElement("b", null, "No likely duplicates right now"), /*#__PURE__*/React.createElement("span", {
      className: "adu-muted"
    }, listId ? "Nothing in this list looks doubled up. Try All lists." : "We check your contacts every night and list anyone who seems to be in here twice.")) : this.renderQueue(items, count) : this.renderDone(items, count), this.state.policyOpen ? this.renderPolicyDialog() : null);
  }
}
export default withRouter(DuplicatesComponent);